import '../i18n/autoTranslate';
// practice-library-resources/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import QuickActionButton from "../../components/ui/QuickActionButton";
import AppBackground from "../../components/ui/AppBackground.tsx";

import ResourceCard from "./components/ResourceCard";
import FeaturedCarousel from "./components/FeaturedCarousel";
import FilterSidebar from "./components/FilterSidebar";
import SearchBar from "./components/SearchBar";
import ResourcePreviewModal from "./components/ResourcePreviewModal";
import CategoryFilter from "./components/CategoryFilter";
import ProgressTracker from "./components/ProgressTracker";
import { buildLocalLibraryFeed, mergeResourceLists } from "./data/realLibraryData";
import SEO from "../SEO";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5000";

/* -------------------- PhoneInterviewTest UI Tokens (copied & adapted) -------------------- */

const GlowBackdrop = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0
    bg-[radial-gradient(55%_45%_at_50%_8%,rgba(108,99,255,0.22),transparent_62%),
        radial-gradient(35%_30%_at_86%_78%,rgba(16,185,129,0.16),transparent_60%),
        radial-gradient(30%_28%_at_14%_82%,rgba(236,72,153,0.10),transparent_62%)]"
  />
);

const HairlineGrid = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 opacity-30
    [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)]
    [background-size:26px_26px]"
  />
);

const Glass = ({ className = "", children }) => (
  <div
    className={[
      "relative rounded-[28px] border border-white/10",
      "bg-white/[0.045] backdrop-blur-2xl",
      "shadow-[0_20px_60px_rgba(0,0,0,0.55)] overflow-hidden",
      className,
    ].join(" ")}
  >
    <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.10),transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_60%_0%,rgba(255,255,255,0.08),transparent_62%)]" />
    <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#6C63FF]/12 blur-3xl" />
    <div className="absolute -bottom-28 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
    <div className="relative">{children}</div>
  </div>
);

const Chip = ({ icon: IconComp, children, tone = "neutral" }) => {
  const map = {
    neutral: "bg-white/5 border-white/10 text-white/75",
    purple: "bg-[#6C63FF]/10 border-[#6C63FF]/20 text-[#D7D4FF]",
    green: "bg-emerald-500/10 border-emerald-500/20 text-emerald-200",
    amber: "bg-amber-500/10 border-amber-500/20 text-amber-200",
  };
  return (
    <span
      className={[
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold border",
        "tracking-wide",
        map[tone] || map.neutral,
      ].join(" ")}
    >
      {IconComp ? <IconComp className="h-4 w-4 opacity-90" /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
};

const IconButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-11 w-11 rounded-2xl flex items-center justify-center transition",
      "bg-white/5 border border-white/10 text-white/85 hover:bg-white/10",
      "active:translate-y-[1px]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const PrimaryButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-11 px-5 rounded-2xl font-semibold text-white transition",
      "bg-[linear-gradient(135deg,rgba(108,99,255,0.95),rgba(16,185,129,0.55))]",
      "shadow-[0_0_90px_rgba(108,99,255,0.18)] hover:shadow-[0_0_130px_rgba(108,99,255,0.26)]",
      "border border-white/10",
      "active:translate-y-[1px] active:shadow-[0_0_70px_rgba(108,99,255,0.14)]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const SecondaryButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={[
      "h-11 px-5 rounded-2xl font-semibold transition",
      "bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white",
      "active:translate-y-[1px]",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

/* -------------------- Resource -> Interview Configuration Mapper -------------------- */
const cleanValue = (value) => String(value || "").trim();

const normalizeValue = (value) =>
  cleanValue(value).toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

const difficultyToScore = (difficulty) => {
  const value = normalizeValue(difficulty);
  if (value.includes("advanced") || value.includes("senior")) return 4;
  if (value.includes("beginner") || value.includes("junior")) return 2;
  return 3;
};

const questionToText = (question) => {
  if (typeof question === "string") return question.trim();
  return cleanValue(question?.question || question?.prompt || question?.title);
};

const getResourceQuestions = (resource) =>
  (resource?.questions || [])
    .map(questionToText)
    .filter(Boolean);

const mapQuestionTypeToCategory = (type) => {
  const value = normalizeValue(type);
  if (!value) return null;
  if (value.includes("behavior")) return "behavioral";
  if (value.includes("system")) return "system_design";
  if (value.includes("product") || value.includes("case") || value.includes("priorit")) return "case";
  if (value.includes("debug")) return "debugging";
  if (value.includes("test") || value.includes("qa")) return "testing";
  if (value.includes("research") || value.includes("ux")) return "case";
  if (value.includes("technical") || value.includes("coding")) return "technical";
  if (value.includes("mixed")) return "mixed";
  return value.replace(/\s+/g, "_");
};

const deriveSelectedCategories = (resource) => {
  const categories = new Set();
  const resourceCategory = mapQuestionTypeToCategory(resource?.interviewType || resource?.type);
  if (resourceCategory) categories.add(resourceCategory);

  (resource?.questions || []).forEach((question) => {
    const category = mapQuestionTypeToCategory(question?.type || question?.category || resource?.interviewType);
    if (category) categories.add(category);
  });

  if (categories.has("mixed")) {
    categories.delete("mixed");
    categories.add("behavioral");
    categories.add("technical");
  }

  if (!categories.size) categories.add("behavioral");
  return Array.from(categories);
};

const derivePrimaryInterviewType = (selectedCategories) => {
  if (selectedCategories.includes("behavioral") && selectedCategories.length === 1) return "behavioral";
  return "technical";
};

const isTechnicalResource = (resource) => {
  const directSignals = [
    resource?.interviewType,
    resource?.category,
    resource?.category_id,
    resource?.type,
    resource?.title
  ]
    .map(normalizeValue)
    .filter(Boolean);

  const hasExplicitTechnicalSignal = directSignals.some((value) =>
    value === "technical" ||
    value.includes("technical interview") ||
    value.includes("technical questions") ||
    value.includes("coding") ||
    value.includes("code challenge")
  );

  if (hasExplicitTechnicalSignal) return true;

  const questionTypes = (resource?.questions || [])
    .map((question) => normalizeValue(question?.type || question?.category))
    .filter(Boolean);

  if (!questionTypes.length) return false;

  return questionTypes.every((type) =>
    type === "technical" || type.includes("technical") || type.includes("coding")
  );
};

const deriveDurationMinutes = (resource, questionCount) => {
  const duration = normalizeValue(resource?.duration);
  const minuteMatch = duration.match(/(\d+)\s*(min|minute)/);
  if (minuteMatch) return Math.min(60, Math.max(10, Number(minuteMatch[1])));

  const hourMatch = duration.match(/(\d+)\s*(h|hour)/);
  if (hourMatch) return Math.min(60, Math.max(30, Number(hourMatch[1]) * 60));

  return Math.min(60, Math.max(15, (questionCount || 1) * 7));
};

const buildFinalInterviewConfiguration = (resource) => {
  const customQuestions = getResourceQuestions(resource);
  const selectedCategories = deriveSelectedCategories(resource);
  const primaryInterviewType = derivePrimaryInterviewType(selectedCategories);
  const difficultyScore = difficultyToScore(resource?.difficulty || resource?.skillLevel);
  const selectedRole = cleanValue(resource?.role === "All Roles" ? "" : resource?.role);

  return {
    selectedType: primaryInterviewType,
    selectedIndustry: cleanValue(resource?.industry),
    selectedRole,
    selectedCompanyType: cleanValue(resource?.companyType || resource?.company_type),
    estimatedQuestions: Math.max(1, customQuestions.length),
    personality: {
      selectedPersona: selectedRole
        ? {
            id: `resource-${normalizeValue(selectedRole).replace(/\s+/g, "-") || "interviewer"}`,
            name: `${selectedRole} Interviewer`,
            description: `Interviewer focused on ${cleanValue(resource?.interviewType || primaryInterviewType)} practice.`
          }
        : {},
      tone: 3,
      difficulty: difficultyScore,
      pace: 3,
      pressure: difficultyScore,
      accent: "american"
    },
    selectedCategories,
    selectedCompanies: Array.isArray(resource?.companies) ? resource.companies : [],
    interviewType: primaryInterviewType,
    customQuestions,
    enableFollowUps: true,
    surpriseQuestions: false,
    starMethodEnforcement: selectedCategories.includes("behavioral"),
    followUpIntensity: 3,
    adaptiveDifficulty: false,
    selectedFormat: "phone",
    duration: deriveDurationMinutes(resource, customQuestions.length),
    elevenlabsVoice: null
  };
};

/* -------------------- Tabs (one-page) -------------------- */
const TABS = [
  { key: "all", label: "All", icon: "Grid3X3" },
  { key: "question", label: "Question Bank", icon: "MessageSquareText" },
  { key: "path", label: "Learning Paths", icon: "GraduationCap" },
  { key: "video", label: "Videos", icon: "Play" },
  { key: "pdf", label: "Docs & Guides", icon: "FileText" },
  { key: "pack", label: "Mock Packs", icon: "Layers" },
  { key: "company_kit", label: "Company Kits", icon: "Building2" },
  { key: "saved", label: "Saved", icon: "Bookmark" },
];

export default function PracticeLibraryResources() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  const [categories, setCategories] = useState([{ id: "all", name: "All", count: 0 }]);
  const [activeCategory, setActiveCategory] = useState(categories?.[0] || null);

  const [featuredResources, setFeaturedResources] = useState([]);
  const [resources, setResources] = useState([]);
  const [counts, setCounts] = useState({ tabs: {}, total: 0 });

  const [continueItem, setContinueItem] = useState(null);
  const [smartRecs, setSmartRecs] = useState([]);

  const [selectedResource, setSelectedResource] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(true);

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const chips = useMemo(() => {
    const out = [];
    Object.entries(filters || {}).forEach(([k, vals]) => {
      (vals || []).forEach((v) => out.push({ key: k, value: v }));
    });
    return out;
  }, [filters]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);

      // Real local dataset makes the library useful immediately, even when the API
      // has no seeded resources yet. API resources are merged on top when available.
      const localFeed = buildLocalLibraryFeed({
        tab,
        query: searchQuery || "",
        filters: filters || {},
      });

      try {
        const params = new URLSearchParams({
          tab,
          q: searchQuery || "",
          filters: JSON.stringify(filters || {}),
          limit: "60",
        });

        // NOTE: keep using user_routes.py when available.
        const res = await fetch(`${API_BASE}/api/user/library/feed?${params.toString()}`, {
          headers: { ...authHeaders },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load library");
        if (ignore) return;

        const mergedItems = mergeResourceLists(localFeed.items, data?.items || []);
        const mergedFeatured = mergeResourceLists(localFeed.featured, data?.featured || []);
        const mergedRecommendations = mergeResourceLists(localFeed.recommendations, data?.recommendations || []);

        setCounts(localFeed.counts);
        setFeaturedResources(mergedFeatured);
        setResources(mergedItems);
        setCategories(localFeed.categories);
        setActiveCategory(localFeed.active_category || { id: "all", name: "All", count: 0 });

        setContinueItem(data?.continue || localFeed.continue || null);
        setSmartRecs(mergedRecommendations);
      } catch (e) {
        console.warn("Using local real library feed because /api/user/library/feed is unavailable:", e);
        if (ignore) return;

        setCounts(localFeed.counts);
        setFeaturedResources(localFeed.featured || []);
        setResources(localFeed.items || []);
        setCategories(localFeed.categories || [{ id: "all", name: "All", count: 0 }]);
        setActiveCategory(localFeed.active_category || { id: "all", name: "All", count: 0 });

        setContinueItem(localFeed.continue || null);
        setSmartRecs(localFeed.recommendations || []);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [tab, searchQuery, JSON.stringify(filters), authHeaders]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setFilters((prev) => ({ ...(prev || {}), category_id: [category?.id] }));
  };

  const handleFilterChange = (sectionId, values) => {
    setFilters((prev) => ({ ...(prev || {}), [sectionId]: values }));
  };

  const removeChip = (chip) => {
    setFilters((prev) => {
      const next = { ...(prev || {}) };
      next[chip.key] = (next[chip.key] || []).filter((x) => x !== chip.value);
      if (!next[chip.key]?.length) delete next[chip.key];
      return next;
    });
  };

  const clearAll = () => {
    setFilters({});
    setSearchQuery("");
    setTab("all");
    setActiveCategory({ id: "all", name: "All", count: 0 });
  };

  const handleResourcePreview = async (resource) => {
    setSelectedResource(resource);
    setIsPreviewModalOpen(true);
    // track preview
    try {
      await fetch(`${API_BASE}/api/user/library/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ resource_id: resource?.id, action: "preview" }),
      });
    } catch {}
  };

  const handleStartPractice = async (resource) => {
    const finalInterviewConfiguration = buildFinalInterviewConfiguration(resource);

    try {
      localStorage.setItem(
        "finalInterviewConfiguration",
        JSON.stringify(finalInterviewConfiguration)
      );
    } catch (error) {
      console.warn("Could not save finalInterviewConfiguration:", error);
    }

    try {
      await fetch(`${API_BASE}/api/user/library/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ resource_id: resource?.id, action: "practice" }),
      });
    } catch {}

    const redirectPath =
      resource?.type === "Resume format"
        ? "/resume-builder"
        : isTechnicalResource(resource)
          ? "/tech-interview"
          : "/interview-setup-configuration";

    navigate(redirectPath, {
      state: {
        resourceId: resource?.id,
        resourceType: resource?.type,
        resourceTitle: resource?.title,
        role: resource?.role,
        interviewType: finalInterviewConfiguration.interviewType,
        difficulty: resource?.difficulty,
        questions: resource?.questions || [],
        finalInterviewConfiguration,
        sourceResource: resource,
      },
    });
  };

  const handleBookmark = async (resourceId, isBookmarked) => {
    try {
      await fetch(`${API_BASE}/api/user/library/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ resource_id: resourceId, save: isBookmarked }),
      });
    } catch {}
  };

  const filteredResources = useMemo(() => {
    // backend already filters, this keeps fast typing UX
    if (!searchQuery) return resources;
    const q = searchQuery.toLowerCase();
    return (resources || []).filter(
      (r) =>
        (r.title || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q)
    );
  }, [resources, searchQuery]);

  // mock user progress (keep your component)
  const userProgress = {
    completed: 18,
    total: 58,
    inProgress: 7,
    streak: 5,
    totalTime: 12,
    weeklyCompleted: 3,
    weeklyGoal: 5,
    recentAchievements: ["Consistency Streak", "Behavioral Pro", "Fast Learner"],
  };

  return (
    <AppBackground>
      <div className="min-h-screen w-full text-white relative overflow-hidden">
        <GlowBackdrop />
        <HairlineGrid />
        <SEO
  title="Interview Library — InterviewGenieAI"
  description="Access your saved interview sessions, feedback history, questions, answers, and performance insights inside InterviewGenieAI."
  canonical="https://interviewgenieai.com/library"
  noindex={true}
/>
        {/* Sticky mission bar (Phone palette) */}
        <div className="sticky top-0 z-40 border-b border-white/10 bg-black/35 backdrop-blur-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl border border-white/10 bg-white/[0.06] flex items-center justify-center">
                  <Icon name="Sparkles" size={18} />
                </div>
                <div>
                  <div className="text-xl font-semibold tracking-tight">Library</div>
                  <div className="text-xs text-white/60">
                    Discover → Save → Practice. Fast.
                  </div>
                </div>
              </div>

              <div className="flex-1 lg:max-w-[620px]">
                <SearchBar
                  onSearch={(q) => setSearchQuery(q)}
                  onFilterToggle={() => setIsFilterSidebarOpen(true)}
                />
              </div>

              <div className="flex items-center gap-2 justify-end">
                <IconButton onClick={() => setIsFilterSidebarOpen(true)} className="lg:hidden">
                  <Icon name="SlidersHorizontal" size={18} />
                </IconButton>

                <SecondaryButton onClick={() => setTab("saved")} className="hidden sm:inline-flex items-center gap-2">
                  <Icon name="Bookmark" size={16} />
                  Saved
                </SecondaryButton>

                <PrimaryButton onClick={() => navigate("/interview-setup-configuration")} className="hidden sm:inline-flex items-center gap-2">
                  <Icon name="Zap" size={16} />
                  Practice now
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-7 relative z-10">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={[
                  "px-4 py-2.5 rounded-2xl border text-sm font-semibold flex items-center gap-2 transition",
                  tab === t.key
                    ? "bg-white/10 border-white/20 text-white"
                    : "bg-white/5 border-white/10 text-white/75 hover:bg-white/10 hover:text-white",
                ].join(" ")}
              >
                <Icon name={t.icon} size={18} />
                {t.label}
                {counts?.tabs?.[t.key] != null && (
                  <span className="ml-1 px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-[11px] text-white/70">
                    {counts.tabs[t.key]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Category row */}
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />

          {/* Chips row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {chips.length ? (
                chips.map((c, idx) => (
                  <button
                    key={`${c.key}:${c.value}:${idx}`}
                    onClick={() => removeChip(c)}
                    className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10 transition flex items-center gap-2"
                  >
                    <span className="text-white/60">{c.key}</span>
                    <span className="font-semibold">{c.value}</span>
                    <Icon name="X" size={14} />
                  </button>
                ))
              ) : (
                <div className="text-xs text-white/55 flex items-center gap-2">
                  <Icon name="Filter" size={16} />
                  Use filters for faster discovery.
                </div>
              )}
            </div>

            {(chips.length || searchQuery) ? (
              <SecondaryButton onClick={clearAll}>Clear all</SecondaryButton>
            ) : null}
          </div>

          {/* Layout: filters + results */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Desktop filters */}
            <div className="hidden lg:block lg:col-span-3">
              <div className="sticky top-28">
                <FilterSidebar
                  isOpen={true}
                  onClose={() => {}}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={() => setFilters({})}
                  categories={categories}
                  activeCategory={activeCategory}
                  onCategoryChange={handleCategoryChange}
                />
              </div>
            </div>

            <div className="lg:col-span-9 space-y-8">
              {/* Featured */}
              <Glass className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm font-semibold">Featured</div>
                    <div className="text-xs text-white/60 mt-1">High-impact curated resources.</div>
                  </div>
                  <Chip tone="purple">Curated</Chip>
                </div>
                <FeaturedCarousel
                  featuredResources={featuredResources}
                  onResourceClick={handleResourcePreview}
                />
              </Glass>

              {/* Results */}
              <Glass className="p-6">
                <div className="flex items-end justify-between gap-3 mb-5">
                  <div>
                    <div className="text-sm font-semibold">
                      {activeCategory?.name || "Library"}{" "}
                      <span className="text-white/50 font-normal">
                        • {filteredResources.length} results
                      </span>
                    </div>
                    <div className="text-xs text-white/60 mt-1">Fast: Open → Save → Practice.</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <IconButton onClick={() => setViewMode("grid")} className={viewMode === "grid" ? "bg-white/10" : ""}>
                      <Icon name="Grid3X3" size={18} />
                    </IconButton>
                    <IconButton onClick={() => setViewMode("list")} className={viewMode === "list" ? "bg-white/10" : ""}>
                      <Icon name="List" size={18} />
                    </IconButton>
                  </div>
                </div>

                {filteredResources.length ? (
                  <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" : "space-y-4"}>
                    {filteredResources.map((resource) => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        onBookmark={handleBookmark}
                        onPreview={handleResourcePreview}
                        onStartPractice={handleStartPractice}
                        className={viewMode === "list" ? "w-full" : ""}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
                    <div className="font-semibold">No results</div>
                    <div className="text-xs text-white/60 mt-1">Try clearing filters or searching fewer keywords.</div>
                    <div className="mt-4 flex justify-center gap-2">
                      <PrimaryButton onClick={() => setFilters({})}>Clear filters</PrimaryButton>
                      <SecondaryButton onClick={() => setSearchQuery("")}>Clear search</SecondaryButton>
                    </div>
                  </div>
                )}
              </Glass>

              {/* Practice Bridge */}
              <Glass className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="text-lg font-semibold">Turn learning into performance</div>
                    <div className="text-sm text-white/60 mt-1">
                      Practice immediately and get structured feedback.
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PrimaryButton onClick={() => navigate("/interview-setup-configuration")}>
                      Start AI Interview
                    </PrimaryButton>
                    <SecondaryButton onClick={() => navigate("/interview-setup-configuration", { state: { mode: "behavioral" } })}>
                      Behavioral
                    </SecondaryButton>
                    <SecondaryButton onClick={() => navigate("/interview-setup-configuration", { state: { mode: "technical" } })}>
                      Technical
                    </SecondaryButton>
                  </div>
                </div>
              </Glass>
            </div>
          </div>

          {/* HERO (Continue + Smart Recs) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <Glass className="lg:col-span-7 p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">Continue your path</div>
                  <div className="text-xs text-white/60 mt-1">Pick up where you left off.</div>
                </div>
                <Chip tone="purple">Coach mode</Chip>
              </div>

              <div className="mt-4">
                {loading ? (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 animate-pulse">
                    <div className="h-4 w-2/3 bg-white/10 rounded mb-3" />
                    <div className="h-3 w-1/3 bg-white/10 rounded mb-3" />
                    <div className="h-2 w-full bg-white/10 rounded" />
                  </div>
                ) : continueItem ? (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold">{continueItem.title}</div>
                        <div className="text-xs text-white/60 mt-1">
                          {continueItem.type} • {continueItem.difficulty || "—"} • {continueItem.duration || "—"}
                        </div>
                      </div>
                      <Chip tone="green">{continueItem.progress ? `${continueItem.progress}%` : "In progress"}</Chip>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-[linear-gradient(135deg,rgba(108,99,255,0.95),rgba(16,185,129,0.55))]"
                        style={{ width: `${continueItem.progress || 35}%` }}
                      />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <PrimaryButton onClick={() => handleResourcePreview(continueItem)}>Continue</PrimaryButton>
                      <SecondaryButton onClick={() => handleStartPractice(continueItem)}>Practice in AI Interview</SecondaryButton>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="font-semibold">No progress yet</div>
                    <div className="text-xs text-white/60 mt-1">
                      Start a pack or question drill — we’ll track and recommend next.
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <PrimaryButton onClick={() => setTab("pack")}>Explore Packs</PrimaryButton>
                      <SecondaryButton onClick={() => setTab("question")}>Question Bank</SecondaryButton>
                    </div>
                  </div>
                )}
              </div>
            </Glass>

            <Glass className="lg:col-span-5 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">Smart recommendations</div>
                  <div className="text-xs text-white/60 mt-1">Based on your activity.</div>
                </div>
                <Chip tone="amber">Personalized</Chip>
              </div>

              <div className="mt-4 grid gap-3">
                {loading ? (
                  <>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 animate-pulse h-20" />
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 animate-pulse h-20" />
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 animate-pulse h-20" />
                  </>
                ) : (smartRecs || []).slice(0, 3).map((r) => (
                  <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="font-semibold text-sm">{r.title}</div>
                    <div className="text-xs text-white/60 mt-1 line-clamp-2">{r.description || "Recommended for you"}</div>
                    <div className="mt-3 flex gap-2">
                      <SecondaryButton onClick={() => handleResourcePreview(r)}>Open</SecondaryButton>
                      <PrimaryButton onClick={() => handleStartPractice(r)}>Practice</PrimaryButton>
                    </div>
                  </div>
                ))}
                {!loading && (!smartRecs || smartRecs.length === 0) && (
                  <div className="text-xs text-white/60">Practice a few items to unlock recommendations.</div>
                )}
              </div>

              <div className="mt-5">
                <ProgressTracker userProgress={userProgress} />
              </div>
            </Glass>
          </div>

        </div>

        {/* Mobile filter drawer */}
        <FilterSidebar
          isOpen={isFilterSidebarOpen}
          onClose={() => setIsFilterSidebarOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={() => setFilters({})}
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          className="lg:hidden"
        />

        <ResourcePreviewModal
          resource={selectedResource}
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          onStartPractice={handleStartPractice}
          onBookmark={handleBookmark}
        />

        <QuickActionButton onQuickStart={() => navigate("/interview-setup-configuration")} />
      </div>
    </AppBackground>
  );
}
